import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Users, Plus, X, Archive, Search, Loader2 } from "lucide-react";
import { customerFormSchema, type CustomerFormInput } from "@finora/validation";
import type { CustomerDto } from "@finora/types";
import { ApiError } from "@finora/api-client";
import { Button, EmptyState, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";

function CustomerFormModal({
  customer,
  onClose,
}: {
  customer: CustomerDto | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CustomerFormInput>({
    resolver: zodResolver(customerFormSchema),
    defaultValues: {
      name: customer?.name ?? "",
      taxId: customer?.taxId ?? "",
      registrationNumber: customer?.registrationNumber ?? "",
      jbkjs: customer?.jbkjs ?? "",
      isVatRegistered: customer?.isVatRegistered ?? true,
      hasEInvoice: customer?.hasEInvoice ?? false,
      email: customer?.email ?? "",
      phone: customer?.phone ?? "",
      address: customer?.address ?? "",
      houseNumber: customer?.houseNumber ?? "",
      postalCode: customer?.postalCode ?? "",
      city: customer?.city ?? "",
      buyerIdType: customer?.buyerIdType ?? "10",
      buyerIdValue: customer?.buyerIdValue ?? "",
    },
  });

  const [isGovernmentEntity, setIsGovernmentEntity] = useState(!!customer?.jbkjs);
  const [lookupOk, setLookupOk] = useState(false);
  const taxIdValue = watch("taxId");

  /**
   * "Pronađi po PIB-u" (isti obrazac kao Onboarding/CompanyStep.tsx) -
   * popuni naziv/matični broj/adresu/telefon/email sa CompanyWall-a.
   * houseNumber se NAMERNO ne popunjava (adresa je jedan string koji se ne
   * može pouzdano razdvojiti na ulicu i broj) - korisnik ga unosi ručno.
   */
  const lookup = useMutation({
    mutationFn: () => api.lookupCompanyByPib(String(taxIdValue ?? "")),
    onSuccess: (result) => {
      if (result.name) setValue("name", result.name);
      if (result.registrationNumber) setValue("registrationNumber", result.registrationNumber);
      if (result.street || result.address) setValue("address", result.street ?? result.address ?? "");
      if (result.houseNumber) setValue("houseNumber", result.houseNumber);
      if (result.postalCode) setValue("postalCode", result.postalCode);
      if (result.city) setValue("city", result.city);
      if (result.phone) setValue("phone", result.phone);
      if (result.email) setValue("email", result.email);
      setLookupOk(true);
    },
  });

  const save = useMutation({
    mutationFn: (data: CustomerFormInput) =>
      customer ? api.updateCustomer(customer.id, data) : api.createCustomer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <form
        onSubmit={handleSubmit((data) => save.mutate(data))}
        className="flex max-h-[90vh] w-full max-w-md flex-col overflow-auto rounded-xl bg-surface shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">
            {customer ? "Izmena kupca" : "Novi kupac"}
          </h2>
          <button
            type="button"
            aria-label="Zatvori"
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Naziv / ime kupca *
            </label>
            <input
              {...register("name")}
              className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
            {errors.name && (
              <p className="mt-1 text-xs text-red-600">{errors.name.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">PIB</label>
              <div className="mt-1 flex gap-2">
                <input
                  {...register("taxId", {
                    onChange: () => setLookupOk(false),
                  })}
                  className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => lookup.mutate()}
                  disabled={lookup.isPending || String(taxIdValue ?? "").replace(/\D/g, "").length !== 9}
                  className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-border text-slate-500 hover:bg-slate-50 disabled:opacity-40"
                  title="Pronađi po PIB-u"
                >
                  {lookup.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </button>
              </div>
              {lookupOk && (
                <p className="mt-1 text-xs text-emerald-600">Podaci su pronađeni i popunjeni.</p>
              )}
              {lookup.isError && (
                <p className="mt-1 text-xs text-red-600">
                  {lookup.error instanceof ApiError
                    ? lookup.error.response.message
                    : "Firma nije pronađena - unesite podatke ručno."}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Matični broj</label>
              <input
                {...register("registrationNumber")}
                className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
            </div>
          </div>

          <div className="space-y-2 rounded-lg border border-border bg-app-bg p-3">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={isGovernmentEntity}
                onChange={(e) => {
                  setIsGovernmentEntity(e.target.checked);
                  if (!e.target.checked) {
                    setValue("jbkjs", "");
                  }
                }}
                className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
              />
              Državna firma / javno preduzeće (ima JBKJS)
            </label>
            {isGovernmentEntity && (
              <div>
                <label className="block text-xs font-medium text-slate-500">JBKJS</label>
                <input
                  {...register("jbkjs")}
                  className="mt-1 h-9 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                />
              </div>
            )}

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                {...register("isVatRegistered")}
                className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
              />
              Obveznik PDV-a
            </label>

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                {...register("hasEInvoice")}
                className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
              />
              Ima eFakture (registrovan na SEF-u)
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700">Email</label>
            <input
              {...register("email")}
              className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
            {errors.email && (
              <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700">Telefon</label>
            <input
              {...register("phone")}
              className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700">Ulica</label>
              <input
                {...register("address")}
                className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Broj</label>
              <input
                {...register("houseNumber")}
                className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">Poštanski broj</label>
              <input
                {...register("postalCode")}
                className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Mesto</label>
              <input
                {...register("city")}
                className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Identifikacija na fisk. računu
              </label>
              <select
                {...register("buyerIdType")}
                className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              >
                <option value="10">10: PIB - domaće pravno lice</option>
                <option value="11">11: JMBG - domaće fiz. lice, samostalna delatnost</option>
                <option value="12">12: PIB i JBKJS (PIB:JBKJS)</option>
                <option value="13">13: Kod penzionerske kartice</option>
                <option value="14">14: PIB - poljoprivredno gazdinstvo</option>
                <option value="15">15: JMBG - poljoprivredno gazdinstvo</option>
                <option value="16">16: BPG - broj poljoprivrednog gazdinstva</option>
                <option value="20">20: Broj lične karte - domaće fiz. lice</option>
                <option value="21">21: Broj izbegličke legitimacije</option>
                <option value="22">22: EBS - strano fiz. lice sa boravkom u Srbiji</option>
                <option value="23">23: Broj pasoša - domaće fiz. lice</option>
                <option value="30">30: Broj pasoša - strano fiz. lice</option>
                <option value="31">31: Broj diplomatske legitimacije/LK</option>
                <option value="32">32: Lična karta MKD (Makedonija)</option>
                <option value="33">33: Lična karta MNE (Crna Gora)</option>
                <option value="34">34: Lična karta ALB (Albanija)</option>
                <option value="35">35: Lična karta BIH</option>
                <option value="36">36: Lična karta - EU/Švajcarska/Norveška/Island</option>
                <option value="40">40: Poreski ID iz strane države (TIN)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Vrednost</label>
              <input
                {...register("buyerIdValue")}
                placeholder="npr. PIB ili broj dokumenta"
                className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
            </div>
          </div>
          <p className="text-xs text-slate-400">
            Koristi se za polje "ID kupca" na fiskalnom računu. Ako ostavite prazno, a kupac ima PIB,
            automatski se koristi "10:PIB".
          </p>

          {save.isError && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              Došlo je do greške prilikom čuvanja. Pokušajte ponovo.
            </p>
          )}
        </div>

        <div className="flex gap-3 border-t border-border px-5 py-4">
          <Button type="button" variant="secondary" className="flex-1" onClick={onClose}>
            Otkaži
          </Button>
          <Button type="submit" className="flex-1" disabled={isSubmitting || save.isPending}>
            Sačuvaj
          </Button>
        </div>
      </form>
    </div>
  );
}

export function CustomersPage() {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<CustomerDto | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const timeout = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const { data: customers, isLoading } = useQuery({
    queryKey: ["customers", search],
    queryFn: () => api.listCustomers(search || undefined),
  });

  const archive = useMutation({
    mutationFn: (id: string) => api.archiveCustomer(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["customers"] }),
  });

  function openCreate() {
    setEditingCustomer(null);
    setModalOpen(true);
  }

  function openEdit(customer: CustomerDto) {
    setEditingCustomer(customer);
    setModalOpen(true);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Kupci</h1>
          <p className="text-sm text-slate-500">Lista kupaca za fakturisanje i ponude</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4" aria-hidden />
          Novi kupac
        </Button>
      </div>

      <label className="relative block max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Pretraži po nazivu, PIB-u, matičnom broju ili emailu"
          className="w-full rounded-lg border border-border bg-surface py-2 pl-9 pr-3 text-sm focus:border-brand focus:outline-none"
        />
      </label>

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        {isLoading ? (
          <div className="space-y-2 p-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !customers || customers.length === 0 ? (
          <div className="p-5">
            <EmptyState
              icon={Users}
              title="Još nema kupaca"
              description="Dodajte prvog kupca dugmetom iznad."
            />
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Naziv</th>
                <th>PIB</th>
                <th>Matični broj</th>
                <th>PDV</th>
                <th>eFakture</th>
                <th>Email</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr key={c.id} className="cursor-pointer hover:bg-slate-50">
                  <td className="font-medium text-slate-800" onClick={() => openEdit(c)}>
                    {c.name}
                    {c.jbkjs && (
                      <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                        JBKJS
                      </span>
                    )}
                  </td>
                  <td className="text-slate-500" onClick={() => openEdit(c)}>
                    {c.taxId ?? "—"}
                  </td>
                  <td className="text-slate-500" onClick={() => openEdit(c)}>
                    {c.registrationNumber ?? "—"}
                  </td>
                  <td className="text-slate-500" onClick={() => openEdit(c)}>
                    {c.isVatRegistered ? "Da" : "Ne"}
                  </td>
                  <td className="text-slate-500" onClick={() => openEdit(c)}>
                    {c.hasEInvoice ? "Da" : "Ne"}
                  </td>
                  <td className="text-slate-500" onClick={() => openEdit(c)}>
                    {c.email ?? "—"}
                  </td>
                  <td>
                    <button
                      aria-label="Arhiviraj kupca"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Arhivirati kupca "${c.name}"?`)) {
                          archive.mutate(c.id);
                        }
                      }}
                      className="text-slate-400 hover:text-red-600"
                    >
                      <Archive className="h-4 w-4" aria-hidden />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalOpen && (
        <CustomerFormModal customer={editingCustomer} onClose={() => setModalOpen(false)} />
      )}
    </div>
  );
}
